import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Input, Segmented, Tag, Tooltip } from 'antd';
import { SearchOutlined, WhatsAppOutlined, CopyOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getRelanceRfm } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeResponsiveTable, NdoeDataCard,
  NdoeStatusBadge, NdoeError, NdoeLoading, useIsDesktop,
} from '../../components/ndoe';
import { money } from './format';
import './rapports.css';

// Segments RFM (mêmes clés que l'API). L'ordre = priorité d'action.
const SEGMENTS = {
  endormie: { label: 'Endormies à réveiller', tone: 'warn', aide: 'Silencieuses depuis 61–180 j : elles achetaient, elles ne sont pas perdues — LE cœur de la relance.' },
  perdue: { label: 'Perdues ?', tone: 'neutral', aide: 'Plus de 180 j sans achat : reconquête au cas par cas, par valeur décroissante.' },
  vip: { label: 'VIP fidèles', tone: 'gold', aide: 'Achètent souvent ET récemment : à choyer (avant-premières, nouveautés).' },
  nouvelle: { label: 'Nouvelles', tone: 'info', aide: '1er achat récent : le 2e achat crée la fidélité — un merci + une suggestion.' },
  active: { label: 'Actives', tone: 'ok', aide: 'Achat récent : rien à faire, ne pas sur-solliciter.' },
};

// Numéro → lien WhatsApp : wa.me exige l'indicatif sans « + » ni zéros de tête.
// Formats en base : +243… (majorité), 243…, 08… (local → préfixe RDC).
const waNumero = (tel) => {
  if (!tel) return null;
  let d = String(tel).replace(/\D/g, '');
  if (d.startsWith('0')) d = `243${d.slice(1)}`;
  return d.length >= 9 ? d : null;
};

const ilYA = (j) => (j === null || j === undefined ? '—' : j === 0 ? "aujourd'hui" : `il y a ${j} j`);
const fmtDate = (v) => {
  if (!v) return '—';
  const [y, m, j] = String(v).slice(0, 10).split('-');
  return j ? `${j}/${m}/${y}` : v;
};
const goutsTxt = (c) => (c.modeles_aimes || [])
  .map((g) => `${g.modele}${g.taille ? ` (P${g.taille})` : ''}`)
  .join(', ');

// Fiche courte à coller dans WhatsApp / un carnet de relance.
const ligneCopie = (c) => [
  `${c.nom} — ${c.telephone || 'sans téléphone'}`,
  `Dernier achat : ${fmtDate(c.derniere_vente)} (${ilYA(c.recence_jours)}) · ${c.jours_achat} achat(s) · ${money(c.ca_total)}`,
  goutsTxt(c) ? `Aime : ${goutsTxt(c)}${c.pointure_preferee ? ` · pointure P${c.pointure_preferee}` : ''}` : null,
].filter(Boolean).join('\n');

export default function RelanceClientes() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [segment, setSegment] = useState('endormie');
  const [q, setQ] = useState('');
  // Pagination CONTRÔLÉE (desktop ET mobile) : PagerMobile exige current/total/
  // onChange — sans eux, le mobile rendait les 407 « perdues » d'un bloc.
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;
  const isDesktop = useIsDesktop();
  useEffect(() => { setPage(1); }, [segment, q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getRelanceRfm();
      setPayload(data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger le rapport de relance.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const kpis = payload?.kpis || {};
  const lignes = useMemo(() => {
    const toutes = payload?.clientes || [];
    const filtre = q.trim().toLowerCase();
    return toutes
      .filter((c) => (segment === 'toutes' ? true : c.segment === segment))
      .filter((c) => (!filtre ? true
        : `${c.nom || ''} ${c.telephone || ''} ${c.commune || ''}`.toLowerCase().includes(filtre)));
  }, [payload, segment, q]);

  const copier = async (texte, message) => {
    try {
      await navigator.clipboard.writeText(texte);
      toast.success(message);
    } catch (e) { toast.error('Copie impossible dans ce navigateur.'); }
  };

  const segBadge = (s) => {
    const d = SEGMENTS[s] || { label: s, tone: 'neutral' };
    return <NdoeStatusBadge kind="custom" tone={d.tone} label={d.label} />;
  };

  const actionsFor = (c) => {
    const wa = waNumero(c.telephone);
    return [
      wa && (
        <Tooltip key="w" title="Ouvrir la conversation WhatsApp">
          <a className="rl-wa" href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer">
            <WhatsAppOutlined /> WhatsApp
          </a>
        </Tooltip>
      ),
      <NdoeActionButton key="c" size="sm" variant="secondary" icon={<CopyOutlined />}
        onClick={() => copier(ligneCopie(c), `Fiche de ${c.nom} copiée.`)}>Copier</NdoeActionButton>,
    ].filter(Boolean);
  };

  const columns = [
    {
      title: 'Cliente', key: 'nom',
      render: (t, c) => (
        <div className="rl-nom">
          <b>{c.nom}</b>
          <small>{[c.telephone, c.commune].filter(Boolean).join(' · ') || '—'}</small>
        </div>
      ),
    },
    { title: 'Segment', key: 'seg', render: (t, c) => segBadge(c.segment) },
    {
      title: 'Dernier achat', key: 'da',
      render: (t, c) => (
        <div className="rl-nom">
          <b className="num">{fmtDate(c.derniere_vente)}</b>
          <small>{ilYA(c.recence_jours)}</small>
        </div>
      ),
    },
    { title: 'Achats', dataIndex: 'jours_achat', key: 'ja', align: 'right', render: (v) => <span className="num">{v}</span> },
    { title: 'CA total', dataIndex: 'ca_total', key: 'ca', align: 'right', render: (v) => <b className="num">{money(v)}</b> },
    // « / achat » = par jour d'achat (colonne Achats) — cohérent ligne à ligne.
    { title: 'Panier / achat', dataIndex: 'panier_moyen', key: 'pm', align: 'right', render: (v) => <span className="num">{money(v)}</span> },
    {
      title: 'Ce qu’elle aime', key: 'gouts',
      render: (t, c) => (
        <div className="rl-gouts">
          {(c.modeles_aimes || []).slice(0, 2).map((g, i) => (
            <Tag key={i}>{g.modele}{g.taille ? ` · P${g.taille}` : ''}</Tag>
          ))}
          {c.pointure_preferee && <Tag color="gold">P{c.pointure_preferee}</Tag>}
        </div>
      ),
    },
    { title: 'Actions', key: 'act', render: (t, c) => <div className="rl-actions">{actionsFor(c)}</div> },
  ];

  const renderCard = (c) => (
    <NdoeDataCard
      title={c.nom}
      subtitle={`${[c.telephone, c.commune].filter(Boolean).join(' · ')} · dernier achat ${fmtDate(c.derniere_vente)} (${ilYA(c.recence_jours)})`}
      badge={segBadge(c.segment)}
      metrics={[
        { value: money(c.ca_total), label: 'CA total' },
        { value: c.jours_achat, label: 'achats' },
        ...(c.pointure_preferee ? [{ value: `P${c.pointure_preferee}`, label: 'pointure' }] : []),
      ]}
      actions={actionsFor(c)}
    />
  );

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoePageHeader eyebrow="Rapports" title="Qui relancer cette semaine" />
        <NdoeError title="Accès réservé" text="Ce rapport est réservé à l'administration." />
      </NdoePage>
    );
  }

  const segOptions = [
    ...Object.entries(SEGMENTS).map(([k, d]) => ({
      value: k,
      label: `${d.label}${kpis[k] ? ` (${kpis[k].clientes})` : ''}`,
    })),
    { value: 'toutes', label: 'Toutes' },
  ];
  const aide = SEGMENTS[segment]?.aide;

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Qui relancer cette semaine"
        subtitle="Chaque cliente classée par Récence / Fréquence / Montant — avec téléphone et goûts, prête pour un message WhatsApp ciblé."
      />

      {error ? <NdoeError title="Erreur" text={error} onRetry={load} /> : loading ? <NdoeLoading rows={5} /> : (
        <>
          <div className="rl-kpis">
            {Object.entries(SEGMENTS).map(([k, d]) => (
              <button type="button" key={k} className={`rl-kpi ${segment === k ? 'is-actif' : ''}`} onClick={() => setSegment(k)}>
                <span className="rl-kpi__n num">{kpis[k]?.clientes ?? 0}</span>
                <span className="rl-kpi__l">{d.label}</span>
                <span className="rl-kpi__ca num">{money(kpis[k]?.ca_historique || 0)}</span>
              </button>
            ))}
          </div>

          <div className="rl-toolbar">
            <Segmented value={segment} onChange={setSegment} options={segOptions} />
            <Input allowClear className="rl-search" prefix={<SearchOutlined />}
              placeholder="Nom, téléphone, commune…" value={q} onChange={(e) => setQ(e.target.value)} />
            <NdoeActionButton variant="secondary" icon={<CopyOutlined />}
              onClick={() => copier(lignes.slice(0, 50).map(ligneCopie).join('\n\n'), `${Math.min(lignes.length, 50)} fiche(s) copiée(s).`)}>
              Copier la liste
            </NdoeActionButton>
          </div>
          {aide && <p className="rl-aide">{aide}</p>}

          <div className="ndoe-card rl-block">
            <NdoeResponsiveTable
              data={isDesktop ? lignes : lignes.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)}
              columns={columns}
              rowKey="id_client"
              pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total: lignes.length,
                onChange: (p) => setPage(p),
                showTotal: (t) => `${t} cliente(s)`,
              }}
              scroll={{ x: 'max-content' }}
              renderCard={renderCard}
              empty={{ icon: '📱', title: 'Aucune cliente dans ce segment' }}
            />
          </div>

          <p className="rl-note">
            Segments calculés sur tout l'historique (référence : {fmtDate(payload?.reference)}) —
            actif ≤ {payload?.seuils?.active_jours} j · endormie ≤ {payload?.seuils?.endormie_max_jours} j · au-delà : perdue.
            {kpis.jamais_achete ? ` ${kpis.jamais_achete} cliente(s) enregistrée(s) n'ont encore jamais acheté.` : ''}
          </p>
        </>
      )}
    </NdoePage>
  );
}
